import { Box, Stack } from '@mui/material';
import { getFileName, getFileType, RichTextEditorFile } from '@provision/rich-text-editor';
import { attachmentRowSx, getAttachmentItemSx } from './attachment-message-content.utils';
import { FileAttachmentThumbnail } from './file-attachment-thumbnail';

/**
 * Renders staged message attachments (in the edit flow) using the same compact
 * pill UI as the read-only display, but with an inline "×" to remove each one.
 *
 * Unlike the read-only `MessageAttachments`, every file is a uniform removable
 * pill: there's no wide audio player and no "+N more" collapse, because the
 * author is staging attachments rather than consuming them.
 *
 * `getFileName` / `getFileType` abstract over both shapes a `RichTextEditorFile`
 * can take — a freshly-attached `File` and an existing `ConversationAttachment` —
 * so new and existing attachments render identically.
 */

// Existing attachments carry a stable `id`; new `File`s don't, so fall back to
// their (picker-deduplicated) name/size/type. Avoids duplicate React keys when
// two stored attachments happen to share a name, size, and content type.
const getAttachmentKey = (file: RichTextEditorFile): string =>
  'id' in file ? file.id : `${getFileName(file)}-${file.size}-${getFileType(file)}`;

export const EditableMessageAttachments = ({
  files,
  onRemove,
}: {
  files: RichTextEditorFile[];
  onRemove: (index: number) => void;
}) => {
  if (!files.length) {
    return null;
  }

  return (
    <Box sx={{ pt: 0.75, pb: 0.75 }}>
      <Stack direction="row" sx={attachmentRowSx}>
        {files.map((file, index) => (
          <Box
            key={getAttachmentKey(file)}
            data-compact-attachment
            sx={getAttachmentItemSx(false)}
          >
            <FileAttachmentThumbnail
              attachment={{
                contentType: getFileType(file),
                fileName: getFileName(file),
                size: file.size,
              }}
              compact
              onRemove={() => onRemove(index)}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
